<template>
  <ScrollArea
    ref="tagsScrollRef"
    @bottom="nextPage">
    <div
      v-if="forbidden" >
      <h1 class="settings__title">
        Organisation tags
      </h1>
      <Forbidden />
    </div>
    <div
      v-else-if="!forbidden && business && business.name"
      class="settings--limit-width">
      <h1 class="settings__title">
        Organisation tags
      </h1>

      <InfoCard
        v-if="!errorMessage && initialTagsRequestFinished && !tagsExist && !searchQuery && !searchChange"
        :flat-icon="SBIconList.TAGS"
        title="Add tags"
        description="Use tags as labels to classify and group your WhatsApp contacts. You can click the “Add Tag” button below contact number details, inside the conversation, to apply a tag."
        action-text="Add tags"
        @action="openAddTagModal" />

      <Spinner
        v-else-if="!initialTagsRequestFinished"
        class="mt-5"
        :thickness="2" />

      <SettingsNote
        v-else-if="errorMessage"
        :icon="SBIconList.ERROR"
        text="Error loading data..." />

      <template v-else>
        <!-- ACTION BAR -->
        <div
          class="sticky-top" >
          <!-- Search section -->
          <div class="flex space-between settings--box bottom-20">
            <SearchInput
              ref="searchInput"
              v-model="searchQuery"
              placeholder="Search tags..." />

            <b-button
              class="flex center-all"
              @click="openAddTagModal">
              <SBIcon
                class="mr-2"
                :icon="SBIconList.PLUS"
                :color="SBColors.blackPrimary"
                :size="8"/>
              <span>New</span>
            </b-button>
          </div>
        </div>

        <!-- No data info -->
        <div
          v-if="initialTagsRequestFinished && !tagsExist && searchQuery && !tagsLoading"
          class="no-data flex center-all">
          <span class="wrapper flex center-all">
            <span class="emoji">😐</span>
            <p>We couldn’t find anything for <strong>{{ searchQuery }}</strong></p>
          </span>
        </div>

        <Spinner
          v-else-if="searchChange && tagsLoading"
          style="height: 80px;"
          class="my-3 full-width spinner-border-top"
          :size="20"
          :thickness="2" />

        <!-- TABLE -->
        <div
          v-else
          ref="tableGrid"
          class="table__grid clickable bottom-40">
          <span class="column-name sticky-top sticky-second">Name</span>
          <span class="column-name sticky-top sticky-second">Created at</span>
          <span class="column-name sticky-top sticky-second" />

          <span
            v-if="true"
            class="full-width table-count">TAGS ({{ meta.total }})</span>

          <template
            v-for="(tag, index) in aggregatedTags" >
            <!-- Name column cell -->
            <div
              :key="`tag-${index}`"
              class="grid-item"
              :class="[`hover-${index}`, 'flex', 'flex-align-center']"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <p
                class="ellipsis"
                :inner-html.prop="(!tag.name ? 'No name' : tag.name) | boldMatch(searchQuery)" />
            </div>

            <!-- Created at column cell -->
            <div
              :key="`join-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <p
                v-if="tag.created_at"
                class="ellipsis">{{ tag.created_at | tableDate }}</p>
              <p
                v-else
                class="ellipsis no-table-data">No date</p>
            </div>

            <!-- Menu column cell -->
            <div
              :key="`action-${index}`"
              class="grid-item"
              :class="[`hover-${index} flex flex-align-center`]"
              @mouseleave="removeHover(index)"
              @mouseenter="addHover(index)"
              @click="selectRow(tag)" >
              <b-dropdown
                no-caret
                right
                toggle-class="settings__table-action"
                @click.stop.prevent
                @show="menuOpen[index] = true"
                @hide="controlHide($event, index)">
                <template #button-content>
                  <span
                    class="menu-button flex center-all"
                    :class="{active: menuOpen[index]}" />
                </template>
                <b-dropdown-item @click.stop.prevent="editTag(tag)" >Edit</b-dropdown-item>
                <b-dropdown-item @click.stop.prevent="deleteTag(tag)" >Delete</b-dropdown-item>
              </b-dropdown>
            </div>
          </template>
        </div>

        <Spinner
          v-if="!searchChange && tagsLoading"
          style="height: 38px;"
          class="my-3 full-width"
          :size="20"
          :thickness="2" />
      </template>

      <CreateTagModal
        ref="createTagModal"
        :business-id="business.id"
        @reload="reset()"
        @update="updateTag" />

    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import escapeStringRegexp from 'escape-string-regexp';

import ToasterMixin from '@/mixins/Toaster';
import Spinner from '../../common/Spinner';
import ScrollArea from '@/components/common/ScrollArea';
import NoPermissionMixin from '@/mixins/AccessPermission';
import InfoCard from '@/components/common/InfoCard';
import SettingsNote from './partials/SettingsNote';
import SearchInput from '@/components/business/widgets/SearchInput';
import CreateTagModal from '@/components/business/settings/modals/CreateTag.modal';
import UserPermissionsMixin from '@/mixins/UserPermissions.mixin';

export default {
  name: 'TagsSettings',
  components: {
    Spinner,
    ScrollArea,
    InfoCard,
    SettingsNote,
    SearchInput,
    CreateTagModal,
  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
    UserPermissionsMixin,
  ],
  props: {
    business: {
      type: Object,
      required: false,
      default: () => {},
    },
  },
  data() {
    return {
      forbidden: false,
      errorMessage: '',
      tagsExist: false,
      initialTagsRequestFinished: false,
      searchQuery: '',
      menuOpen: [],
      aggregatedTags: [],
      searchChange: false,
      page: 0,
      limit: 50,
      meta: null,
    };
  },
  computed: {
    ...mapGetters({
      currentOrganisation: 'organisation/currentOrganisation',
      tagsLoading: 'contactTag/loading',
      tagsList: 'contactTag/list',
      apiError: 'contactTag/apiError',
    }),
    // accessDenied() {
    //   return this.forbidden || !this.checkPermission('manage_organisation_settings');
    // },
    organisationId() {
      return this.$route.params.organisationId;
    },
  },
  watch: {
    // accessDenied() {
    //   if (!this.accessDenied) {
    //     this.nextPage();
    //   }
    // },
    apiError(error) {
      this.errorMessage = error.data.message;
    },
    contactTagsLoading() {
      // this.resetChanges();
    },
    searchQuery() {
      this.searchChange = true;
      this.reset();
    },
    tagsLoading() {
      if (!this.tagsLoading) {
        if (!this.initialTagsRequestFinished) {
          this.initialTagsRequestFinished = true;
          this.tagsExist = !!this.tagsList.length;
          this.meta = { ...this.tagsList.meta }; // NOTE - total results in paging is acurate only on the first page so this is why we are only using it from initial response
        }
      }
    },
    tagsList() {
      this.aggregatedTags = [...this.aggregatedTags, ...this.tagsList.data];
      this.tagsExist = !!this.aggregatedTags.length;
      if (!this.tagsExist) {
        this.initialTagsRequestFinished = true;
      }
      setTimeout(() => {
        this.loadMoreIfNeeded();
      }, 150);
    },
    business() {
      this.reset();
    },
  },
  mounted() {
    this.finalizeSplashView();
    window.eventBus.$on('403', () => { this.forbidden = true; });
    this.nextPage();
  },
  methods: {
    ...mapActions({
      fetchOrganisationContactTags: 'contactTag/fetchOrganisationContactTags',
      deleteTagAPI: 'contactTag/deleteTag',
      finalizeSplashView: 'organisation/finalizeSplashView',
    }),
    openAddTagModal() {
      this.$refs.createTagModal.show();
    },
    controlHide(event, index) {
      if (this.noHide) {
        this.noHide = false;
        event.preventDefault();
      } else {
        this.menuOpen[index] = false;
      }
    },
    reset() {
      this.page = 0;
      this.aggregatedTags = [];
      this.nextPage();
    },
    removeHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        cell.classList.remove('hover-grid');
        cell.classList.remove('last');
      }
    },
    updateTag(data) {
      const targetTag = this.aggregatedTags.find(tag => tag.id === data.id);
      targetTag.name = data.name;
    },
    addHover(index) {
      const cells = this.$refs.tableGrid.getElementsByClassName(`hover-${index}`);

      for (let index = 0; index < cells.length; index++) {
        const cell = cells[index];
        if (index === 0) {
          cell.classList.add('first');
        }
        cell.classList.add('hover-grid');
        if (index === cells.length - 1) {
          cell.classList.add('last');
        }
      }
    },
    loadMoreIfNeeded() {
      if (!this.$refs.tagsScrollRef.verticalScrollActive) {
        this.nextPage();
      }
    },
    nextPage() {
      if (!this.initialTagsRequestFinished || (this.page < this.tagsList.meta.last_page) || this.searchChange) {
        this.page++;
        this.fetchOrganisationContactTags({
          organisationId: this.organisationId,
          businessId: this.business.id,
          data: {
            limit: this.limit,
            page: this.page,
            name: this.searchQuery,
            editable: true,
          },
        })
          .finally(() => {
            this.searchChange = false;
          });
      }
    },
    deleteTag(tag) {
      this.$confirmPluginModal.show({
        buttonVariant: 'RED',
        title: 'Delete tag',
        message: `Are you sure you want to delete tag <b>${tag.name}</b>?`,
        confirmButtonText: 'Delete',
        showUndoWarning: false,
        autoClose: 'NO',
        confirmCallback: () => {
          this.deleteTagAPI({
            organisationId: this.organisationId,
            businessId: this.business.id,
            tagId: tag.id,
          }).then((response) => {
            if (response.status === 200) {
              this.showToaster('Tag has been successfully deleted.', null, null, 'OK');
              this.$confirmPluginModal.close();
              this.reset();
            }
          }).catch(() => {
            this.showToaster('Error while deleting tag.', null, null, 'error');
            this.$confirmPluginModal.finishWorking();
          });
        },
      });
    },
    editTag(tag) {
      this.$refs.createTagModal.show(tag);
    },
    selectRow(tag) {
      this.$refs.createTagModal.show(tag);
    },
  },
};
</script>

<style lang="scss" scoped>
.spinner-border-top {
  border-top: 1px solid #e6ebed;
  margin-top: 0 !important;
  padding-top: 40px;
}

.table__grid {
  grid-template-columns: minmax(50px, auto) minmax(0, max-content) min-content !important;

  .full-width {
    grid-column: 1 / 4 !important;
  }

  .no-table-data {
    color: $greyPrimary;
  }
}
</style>
